// src/components/onboarding/CampaignSettingsAnalysis.tsx
import React, { useMemo, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  MapPin,
  Network,
  Eye,
  DollarSign,
} from "lucide-react";
import { cn, formatNumber } from "@/lib/utils";
import { QuickWinCard } from "./QuickWinCard";
import { CopyButton } from "@/components/ui/copy-button";

interface SettingsRow {
  campaign: string;
  campaignId: string;
  status: string;
  channel: string;
  targetContentNetwork: boolean;
  networkType: string;
  geoTargetTypePos?: string;
  impr: number;
  clicks: number;
  cost: number;
  conv: number;
  value: number;
  tcpaTarget?: number;
  troasTarget?: number;
  lostBudget?: number;
}

interface CampaignSettingsAnalysisProps {
  data: SettingsRow[] | undefined;
  onGeoTargetingCountChange?: (count: number) => void;
  onContentNetworkCountChange?: (count: number) => void;
  onSearchPartnersCountChange?: (count: number) => void;
  onBudgetConstrainedCountChange?: (count: number) => void;
  focusedSection?:
    | "geo-targeting"
    | "search-partners"
    | "display-network"
    | "budget"
    | null;
}

export const CampaignSettingsAnalysis: React.FC<
  CampaignSettingsAnalysisProps
> = ({
  data,
  onGeoTargetingCountChange,
  onContentNetworkCountChange,
  onSearchPartnersCountChange,
  onBudgetConstrainedCountChange,
  focusedSection,
}) => {
  // Search Partners Analysis
  const searchPartnersInsights = useMemo(() => {
    const settingsData = (data || []) as SettingsRow[];
    if (!settingsData.length) return { analysis: null };

    // Group by campaign to handle multiple rows per campaign
    const campaignGroups = settingsData.reduce(
      (groups, row) => {
        if (row.channel?.toUpperCase() !== "SEARCH") return groups;

        const key = `${row.campaign}-${row.campaignId}`;
        if (!groups[key]) {
          groups[key] = {
            campaign: row.campaign,
            campaignId: row.campaignId,
            rows: [],
          };
        }
        groups[key].rows.push(row);
        return groups;
      },
      {} as Record<
        string,
        { campaign: string; campaignId: string; rows: SettingsRow[] }
      >,
    );

    // Rest of search partners logic...
    const searchOnlyCampaigns: any[] = [];
    const searchPartnersCampaigns: any[] = [];

    Object.values(campaignGroups).forEach((campGroup) => {
      const hasSearchNetwork = campGroup.rows.some(
        (row) => row.networkType === "SEARCH",
      );
      const hasSearchPartners = campGroup.rows.some(
        (row) => row.networkType === "SEARCH_PARTNERS",
      );

      const totalCost = campGroup.rows.reduce(
        (sum, row) => sum + (row.cost || 0),
        0,
      );
      const totalConv = campGroup.rows.reduce(
        (sum, row) => sum + (row.conv || 0),
        0,
      );

      const campaignData = {
        campaign: campGroup.campaign,
        totalCost,
        totalConv,
      };

      if (hasSearchNetwork && !hasSearchPartners) {
        searchOnlyCampaigns.push(campaignData);
      } else if (hasSearchNetwork && hasSearchPartners) {
        searchPartnersCampaigns.push(campaignData);
      }
    });

    const searchOnlyPerf = {
      campaigns: searchOnlyCampaigns.length,
      avgCpa:
        searchOnlyCampaigns.reduce((sum, c) => sum + c.totalConv, 0) > 0
          ? searchOnlyCampaigns.reduce((sum, c) => sum + c.totalCost, 0) /
            searchOnlyCampaigns.reduce((sum, c) => sum + c.totalConv, 0)
          : 0,
    };

    const searchPartnersPerf = {
      campaigns: searchPartnersCampaigns.length,
      avgCpa:
        searchPartnersCampaigns.reduce((sum, c) => sum + c.totalConv, 0) > 0
          ? searchPartnersCampaigns.reduce((sum, c) => sum + c.totalCost, 0) /
            searchPartnersCampaigns.reduce((sum, c) => sum + c.totalConv, 0)
          : 0,
    };

    const recommendation =
      searchPartnersPerf.avgCpa > searchOnlyPerf.avgCpa * 1.2
        ? "disable"
        : "keep";

    return {
      analysis: {
        searchOnly: searchOnlyPerf,
        searchPartners: searchPartnersPerf,
        recommendation,
      },
    };
  }, [data]);

  // Content Network Analysis
  const contentNetworkInsights = useMemo(() => {
    const settingsData = (data || []) as SettingsRow[];
    if (!settingsData.length) return { issues: [], summary: null };

    const campaignGroups = settingsData.reduce(
      (groups, row) => {
        const key = `${row.campaign}-${row.campaignId}`;
        if (!groups[key]) {
          groups[key] = {
            campaign: row.campaign,
            campaignId: row.campaignId,
            channel: row.channel,
            rows: [],
          };
        }
        groups[key].rows.push(row);
        return groups;
      },
      {} as Record<
        string,
        {
          campaign: string;
          campaignId: string;
          channel: string;
          rows: SettingsRow[];
        }
      >,
    );

    const searchCampaignsWithDisplay = Object.values(campaignGroups).filter(
      (campGroup) => {
        const isSearchChannel =
          campGroup.channel && campGroup.channel.toLowerCase() === "search";
        if (!isSearchChannel) return false;

        const hasContentNetworkRow = campGroup.rows.some(
          (row) =>
            row.networkType === "CONTENT_NETWORK" ||
            row.networkType === "CONTENT" ||
            row.targetContentNetwork === true,
        );

        return hasContentNetworkRow;
      },
    );

    const totalWastedCost = searchCampaignsWithDisplay.reduce(
      (sum, campGroup) => {
        const contentNetworkCost = campGroup.rows
          .filter(
            (row) =>
              row.networkType === "CONTENT_NETWORK" ||
              row.networkType === "CONTENT",
          )
          .reduce((wasteCost, row) => wasteCost + (row.cost || 0), 0);
        return sum + contentNetworkCost;
      },
      0,
    );

    return {
      issues: searchCampaignsWithDisplay,
      summary:
        searchCampaignsWithDisplay.length > 0
          ? {
              count: searchCampaignsWithDisplay.length,
              estimatedWaste: totalWastedCost,
            }
          : null,
    };
  }, [data]);

  // Geo Targeting Analysis
  const geoTargetingInsights = useMemo(() => {
    const settingsData = (data || []) as SettingsRow[];
    if (!settingsData.length) return { issues: [], summary: null };

    const campaignGroups = settingsData.reduce(
      (groups, row) => {
        const key = `${row.campaign}-${row.campaignId}`;
        if (!groups[key]) {
          groups[key] = {
            campaign: row.campaign,
            geoTargetTypePos: row.geoTargetTypePos,
            rows: [],
          };
        }
        groups[key].rows.push(row);
        return groups;
      },
      {} as Record<
        string,
        { campaign: string; geoTargetTypePos: string; rows: SettingsRow[] }
      >,
    );

    const presenceOrInterestCampaigns = Object.values(campaignGroups).filter(
      (campGroup) => {
        return campGroup.geoTargetTypePos === "PRESENCE_OR_INTEREST";
      },
    );

    const totalCost = presenceOrInterestCampaigns.reduce((sum, campGroup) => {
      const campaignTotalCost = campGroup.rows.reduce(
        (campSum, row) => campSum + (row.cost || 0),
        0,
      );
      return sum + campaignTotalCost;
    }, 0);

    return {
      issues: presenceOrInterestCampaigns,
      summary:
        presenceOrInterestCampaigns.length > 0
          ? {
              count: presenceOrInterestCampaigns.length,
              estimatedWaste: totalCost * 0.15,
            }
          : null,
    };
  }, [data]);

  // Impression Share Analysis
  const impressionShareInsights = useMemo(() => {
    const settingsData = (data || []) as SettingsRow[];
    if (!settingsData.length) return { budgetConstrained: [], summary: null };

    const campaigns = settingsData.reduce(
      (acc, row) => {
        const key = `${row.campaign}-${row.campaignId}`;
        if (!acc[key]) {
          acc[key] = {
            campaign: row.campaign,
            impr: 0,
            cost: 0,
            conv: 0,
            lostBudget: 0,
          };
        }

        acc[key].impr += row.impr || 0;
        acc[key].cost += row.cost || 0;
        acc[key].conv += row.conv || 0;

        if ((row as any).lostBudget > acc[key].lostBudget) {
          acc[key].lostBudget = (row as any).lostBudget || 0;
        }

        return acc;
      },
      {} as Record<string, any>,
    );

    const campaignArray = Object.values(campaigns);
    const budgetConstrainedCampaigns = campaignArray.filter(
      (camp) => camp.lostBudget > 0.15 && camp.conv > 0 && camp.cost > 100,
    );

    const additionalConversions = budgetConstrainedCampaigns.reduce(
      (sum, camp) => {
        const currentConvRate = camp.conv / camp.impr;
        const lostImpressions =
          camp.impr * (camp.lostBudget / (1 - camp.lostBudget));
        const potentialConversions = lostImpressions * currentConvRate;
        return sum + potentialConversions;
      },
      0,
    );

    const budgetIncrease = budgetConstrainedCampaigns.reduce((sum, camp) => {
      const increaseNeeded =
        camp.cost * (camp.lostBudget / (1 - camp.lostBudget));
      return sum + increaseNeeded;
    }, 0);

    return {
      budgetConstrained: budgetConstrainedCampaigns,
      summary:
        budgetConstrainedCampaigns.length > 0
          ? {
              count: budgetConstrainedCampaigns.length,
              potentialAdditionalConversions: Math.round(additionalConversions),
              recommendedBudgetIncrease: budgetIncrease,
            }
          : null,
    };
  }, [data]);

  // Notify parent of count changes
  useEffect(() => {
    if (onGeoTargetingCountChange) {
      onGeoTargetingCountChange(geoTargetingInsights.issues.length);
    }
  }, [geoTargetingInsights.issues.length, onGeoTargetingCountChange]);

  useEffect(() => {
    if (onContentNetworkCountChange) {
      onContentNetworkCountChange(contentNetworkInsights.issues.length);
    }
  }, [contentNetworkInsights.issues.length, onContentNetworkCountChange]);

  useEffect(() => {
    if (onSearchPartnersCountChange) {
      // Count campaigns that should disable search partners
      const count =
        searchPartnersInsights.analysis?.recommendation === "disable" ? 1 : 0;
      onSearchPartnersCountChange(count);
    }
  }, [
    searchPartnersInsights.analysis?.recommendation,
    onSearchPartnersCountChange,
  ]);

  useEffect(() => {
    if (onBudgetConstrainedCountChange) {
      onBudgetConstrainedCountChange(
        impressionShareInsights.summary?.count || 0,
      );
    }
  }, [impressionShareInsights.summary?.count, onBudgetConstrainedCountChange]);

  // Filter components based on focused section
  const shouldShowComponent = (componentType: string) => {
    if (!focusedSection) return true; // Show all if no focus
    return focusedSection === componentType;
  };

  const gridClass = focusedSection
    ? "space-y-6"
    : "grid grid-cols-1 lg:grid-cols-2 gap-6";

  return (
    <div className={gridClass}>
      {/* Geo Targeting Issues - Redesigned */}
      {shouldShowComponent("geo-targeting") && (
        <QuickWinCard
          id="geo-targeting-analysis"
          title="Geo Targeting Settings"
          description="Campaigns using 'Presence or Interest' instead of 'Presence' may waste budget"
          icon={<MapPin />}
          severity={geoTargetingInsights.summary?.count ? "medium" : "good"}
        >
          {geoTargetingInsights.summary?.count ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-amber-800">
                      Potential Budget Waste
                    </h4>
                    <p className="text-sm text-amber-700">
                      Estimated waste:{" "}
                      <strong>
                        $
                        {formatNumber(
                          geoTargetingInsights.summary.estimatedWaste,
                          0,
                        )}
                        /week
                      </strong>
                      from broad geo targeting
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge variant="secondary">
                      {geoTargetingInsights.summary.count} campaigns
                    </Badge>
                    <CopyButton
                      text={geoTargetingInsights.issues
                        .map(
                          (issue: any) =>
                            `${issue.campaign}\t${issue.campaignId}\t${issue.geoTargetTypePos}`,
                        )
                        .join("\n")}
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      Copy All Data
                    </CopyButton>
                  </div>
                </div>
              </div>

              {/* Complete Geo Targeting Issues Table */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h5 className="font-medium text-slate-700">
                    All Geo Targeting Issues (
                    {geoTargetingInsights.issues.length} campaigns)
                  </h5>
                  <CopyButton
                    text={geoTargetingInsights.issues
                      .map(
                        (issue: any) =>
                          `${issue.campaign}\t${issue.campaignId}\t${issue.geoTargetTypePos}`,
                      )
                      .join("\n")}
                    variant="outline"
                    size="sm"
                  >
                    Copy All Table Data
                  </CopyButton>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-[1000px]">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-slate-100 rounded-t text-xs font-medium text-slate-700">
                      <div className="col-span-4">Campaign Name</div>
                      <div className="col-span-3">Current Setting</div>
                      <div className="col-span-2 text-center">Weekly Spend</div>
                      <div className="col-span-1 text-center">Priority</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {/* Table Rows - All Geo Issues */}
                    <div className="space-y-1">
                      {geoTargetingInsights.issues.map(
                        (issue: any, index: number) => (
                          <div
                            key={index}
                            className="grid grid-cols-12 gap-2 px-4 py-3 bg-white border-b border-amber-100 hover:bg-amber-50 transition-colors text-sm"
                          >
                            <div className="col-span-4 font-medium text-slate-800">
                              <div className="truncate" title={issue.campaign}>
                                {issue.campaign}
                              </div>
                              <div className="text-xs text-slate-500">
                                ID: {issue.campaignId}
                              </div>
                            </div>
                            <div className="col-span-3 text-slate-600">
                              <Badge
                                variant="outline"
                                className="text-amber-700 border-amber-300"
                              >
                                {issue.geoTargetTypePos}
                              </Badge>
                              <div className="text-xs text-slate-500 mt-1">
                                Should be: "Presence"
                              </div>
                            </div>
                            <div className="col-span-2 text-center text-slate-700">
                              <div className="font-medium">
                                ${formatNumber(issue.cost || 0, 0)}
                              </div>
                              <div className="text-xs text-slate-500">
                                /week
                              </div>
                            </div>
                            <div className="col-span-1 text-center">
                              <Badge variant="secondary" className="text-xs">
                                Medium
                              </Badge>
                            </div>
                            <div className="col-span-2 text-center">
                              <CopyButton
                                text={`${issue.campaign}\t${issue.campaignId}\t${issue.geoTargetTypePos}`}
                                variant="ghost"
                                size="sm"
                              >
                                Copy
                              </CopyButton>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="font-medium text-slate-700">
                  Priority fix: {geoTargetingInsights.issues[0]?.campaign}
                </h5>
                <p className="text-xs text-slate-500 mt-1">
                  {geoTargetingInsights.summary.count > 1 &&
                    `+${geoTargetingInsights.summary.count - 1} more campaigns using broad targeting`}
                </p>
              </div>

              <div className="space-y-2">
                <h5 className="font-medium text-slate-700">
                  Recommended Action
                </h5>
                <p className="text-sm text-slate-600">
                  Change location targeting to "Presence" instead of "Presence
                  or Interest" to target only users physically in your target
                  locations.
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                All campaigns are using optimal geo targeting settings.
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}

      {/* Search Partners Performance - Redesigned */}
      {shouldShowComponent("search-partners") && (
        <QuickWinCard
          id="search-partners-analysis"
          title="Search Partners Analysis"
          description="Comparing performance between Google Search and Search Partners"
          icon={<Network />}
          severity={
            searchPartnersInsights.analysis?.recommendation === "disable"
              ? "medium"
              : "good"
          }
        >
          {searchPartnersInsights.analysis ? (
            searchPartnersInsights.analysis.searchPartners.campaigns > 0 ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-green-800">
                        Performance Comparison
                      </h4>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge variant="secondary">
                        {
                          searchPartnersInsights.analysis.searchPartners
                            .campaigns
                        }{" "}
                        campaigns with partners
                      </Badge>
                      <CopyButton
                        text={`Search Partners Analysis\nSearch Only CPA: $${formatNumber(searchPartnersInsights.analysis.searchOnly.avgCpa, 2)}\nSearch Partners CPA: $${formatNumber(searchPartnersInsights.analysis.searchPartners.avgCpa, 2)}\nRecommendation: ${searchPartnersInsights.analysis.recommendation === "disable" ? "DISABLE Search Partners" : "KEEP Search Partners"}`}
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                      >
                        Copy Analysis
                      </CopyButton>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-green-700">Google Search: </span>
                      <strong>
                        $
                        {formatNumber(
                          searchPartnersInsights.analysis.searchOnly.avgCpa,
                          2,
                        )}{" "}
                        CPA
                      </strong>
                    </div>
                    <div>
                      <span className="text-blue-700">Search Partners: </span>
                      <strong>
                        $
                        {formatNumber(
                          searchPartnersInsights.analysis.searchPartners.avgCpa,
                          2,
                        )}{" "}
                        CPA
                      </strong>
                    </div>
                  </div>
                </div>

                {searchPartnersInsights.analysis.recommendation ===
                  "disable" && (
                  <div>
                    <h5 className="font-medium text-slate-700">
                      Priority fix: Disable Search Partners
                    </h5>
                    <p className="text-xs text-slate-500 mt-1">
                      Partners CPA is{" "}
                      {(
                        (searchPartnersInsights.analysis.searchPartners.avgCpa /
                          searchPartnersInsights.analysis.searchOnly.avgCpa -
                          1) *
                        100
                      ).toFixed(0)}
                      % higher than Google Search
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <h5 className="font-medium text-slate-700">
                    Recommended Action
                  </h5>
                  <p className="text-sm text-slate-600">
                    {searchPartnersInsights.analysis.recommendation ===
                    "disable"
                      ? "Disable Search Partners on campaigns where CPA is significantly higher than Google Search performance."
                      : "Search Partners performance is acceptable. Continue monitoring CPA differences."}
                  </p>
                </div>
              </div>
            ) : (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="font-medium">
                      Search Partners are disabled on all campaigns
                    </div>
                    <div className="text-sm">
                      Your campaigns are configured to only target Google
                      Search, which is the recommended approach for most
                      accounts. Search Partners can sometimes deliver lower
                      quality traffic at higher costs.
                    </div>
                    <div className="text-sm font-medium text-green-700">
                      ✓ No action needed - excellent optimization!
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            )
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Insufficient data to analyze Search Partners performance.
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}

      {/* Display Network Issues - Redesigned */}
      {shouldShowComponent("display-network") && (
        <QuickWinCard
          id="display-network-analysis"
          title="Display Network on Search"
          description="Search campaigns incorrectly targeting the Display Network"
          icon={<Eye />}
          severity={contentNetworkInsights.summary?.count ? "high" : "good"}
        >
          {contentNetworkInsights.summary?.count ? (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-red-800">
                    Budget Leak Detected
                  </h4>
                  <Badge variant="secondary">
                    {contentNetworkInsights.summary.count} campaigns
                  </Badge>
                </div>
                <p className="text-sm text-red-700">
                  Wasted spend:{" "}
                  <strong>
                    $
                    {formatNumber(
                      contentNetworkInsights.summary.estimatedWaste,
                      0,
                    )}
                    /week
                  </strong>
                  on Display Network traffic
                </p>
              </div>

              <div>
                <h5 className="font-medium text-slate-700">
                  Priority fix: {contentNetworkInsights.issues[0]?.campaign}
                </h5>
                <p className="text-xs text-slate-500 mt-1">
                  {contentNetworkInsights.summary.count > 1 &&
                    `+${contentNetworkInsights.summary.count - 1} more search campaigns with Display Network enabled`}
                </p>
              </div>

              <div className="space-y-2">
                <h5 className="font-medium text-slate-700">
                  Recommended Action
                </h5>
                <p className="text-sm text-slate-600">
                  Disable "Display Network" targeting on these search campaigns.
                  Search campaigns should only target Google Search and Search
                  Partners.
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                All search campaigns correctly exclude the Display Network.
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}

      {/* Budget-Constrained Campaigns - Simplified */}
      {shouldShowComponent("budget") && (
        <QuickWinCard
          id="budget-analysis"
          title="Budget-Constrained Campaigns"
          description="High-performing campaigns losing impression share due to budget limits"
          icon={<DollarSign />}
          severity={impressionShareInsights.summary?.count ? "medium" : "good"}
        >
          {impressionShareInsights.summary?.count ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-blue-800">
                    Budget Opportunity
                  </h4>
                  <Badge variant="secondary">
                    {impressionShareInsights.summary.count} campaigns
                  </Badge>
                </div>
                <p className="text-sm text-blue-700">
                  Potential:{" "}
                  <strong>
                    +
                    {
                      impressionShareInsights.summary
                        .potentialAdditionalConversions
                    }{" "}
                    conversions/week
                  </strong>
                  with{" "}
                  <strong>
                    +$
                    {formatNumber(
                      impressionShareInsights.summary.recommendedBudgetIncrease,
                      0,
                    )}
                  </strong>{" "}
                  budget
                </p>
              </div>

              <div>
                {(() => {
                  const topCampaign =
                    impressionShareInsights.budgetConstrained[0];
                  const currentConvRate = topCampaign.conv / topCampaign.impr;
                  const lostImpressions =
                    topCampaign.impr *
                    (topCampaign.lostBudget / (1 - topCampaign.lostBudget));
                  const additionalConversions = Math.round(
                    lostImpressions * currentConvRate,
                  );
                  const additionalSpend = Math.round(
                    topCampaign.cost *
                      (topCampaign.lostBudget / (1 - topCampaign.lostBudget)),
                  );

                  return (
                    <>
                      <h5 className="font-medium text-slate-700">
                        Top opportunity: {topCampaign.campaign}
                      </h5>
                      <p className="text-xs text-slate-500 mt-1">
                        {(topCampaign.lostBudget * 100).toFixed(0)}% lost to
                        budget • +{additionalConversions} potential conversions
                        • +${formatNumber(additionalSpend, 0)} extra spend
                        needed
                        {impressionShareInsights.summary.count > 1 &&
                          ` • +${impressionShareInsights.summary.count - 1} more campaigns affected`}
                      </p>
                    </>
                  );
                })()}
              </div>

              <div className="space-y-2">
                <h5 className="font-medium text-slate-700">
                  Recommended Action
                </h5>
                <p className="text-sm text-slate-600">
                  Increase daily budgets on these high-performing campaigns.
                  They're missing profitable conversions due to budget
                  constraints.
                </p>
              </div>
            </div>
          ) : (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                No campaigns are significantly constrained by budget limits.
              </AlertDescription>
            </Alert>
          )}
        </QuickWinCard>
      )}
    </div>
  );
};
